// filterJsonSchema.js

import {
  PERFORMANCES_ACTIVITY_TYPES,
  PERFORMANCES_CHANNELS,
  PERFORMANCES_LIFE_CYCLE_STATUS,
} from "../../utils/constants";
import {
  codeScanFilterAllJsonSchema,
  codeScanFilterAllUiSchema,
  codeScanFilterJsonSchema,
  codeScanFilterUiSchema,
} from "./mocks/code-scan-mocks";

// Helper functions to reduce code duplication

// Common property builders
const createStringProperty = (title = "Search") => ({
  type: "string",
  title,
});

const createArrayProperty = (options = []) => ({
  type: "array",
  items: {
    type: "string",
    options,
  },
});

const createDateProperty = (title) => ({
  type: "string",
  format: "date",
  title,
});

// Options builders
const createStatusOptions = () =>
  Object.values(PERFORMANCES_LIFE_CYCLE_STATUS).map((status) => ({
    id: status.key,
    displayText: status.value,
  }));

const createActivityTypeOptions = () =>
  PERFORMANCES_ACTIVITY_TYPES.map((activityType) => ({
    id: activityType.key,
    displayText: activityType.value,
  }));

const createChannelOptions = () =>
  Object.values(PERFORMANCES_CHANNELS).map((channel) => ({
    id: channel.key,
    displayText: channel.value,
  }));

// Common property definitions
const commonProperties = {
  ids: createStringProperty(),
  userIds: createStringProperty(),
  profileIds: createStringProperty(),
  externalIdentifier: createStringProperty(),
  engagementIdentifier: createStringProperty(),
  engagementDateFrom: createDateProperty("From Date (Performance Date)"),
  engagementDateTo: createDateProperty("To Date (Performance Date)"),
  engagementStatus: {
    items: {
      type: "string",
      options: createStatusOptions(),
    },
  },
  engagementType: {
    items: {
      type: "string",
      options: createActivityTypeOptions(),
    },
  },
  channel: createArrayProperty(createChannelOptions()),
};

// UI Control builders
const createSearchControl = (property, placeholder, label, options = {}) => ({
  type: "Control",
  scope: `#/properties/${property}`,
  component: "searchInput",
  placeholder,
  label,
  options,
});

const createDateControl = (property, label, extraProps = {}) => ({
  type: "Control",
  scope: `#/properties/${property}`,
  component: "datePickerMaterialUI",
  label,
  ...extraProps,
});

const createMultiSelectControl = (
  property,
  placeholder,
  label,
  component = "multipleSelectPerformanceList"
) => ({
  type: "Control",
  scope: `#/properties/${property}`,
  component,
  selection: "multiple",
  placeholder,
  label,
});

const createBasicControl = (property, placeholder, label, extraProps = {}) => ({
  type: "Control",
  scope: `#/properties/${property}`,
  placeholder,
  label,
  ...extraProps,
});

// Common UI elements
const commonUIElements = {
  searchInputs: {
    ids: createSearchControl("ids", "Search by ID", "ID", { debounce: true }),
    userIds: createSearchControl("userIds", "Search by User ID", "User ID", {
      debounce: true,
    }),
    profileIds: createSearchControl(
      "profileIds",
      "Search by Profile ID",
      "Profile ID",
      {
        debounce: true,
      }
    ),
    engagementIdentifier: createSearchControl(
      "engagementIdentifier",
      "Search by External ID",
      "External ID",
      { debounce: true }
    ),
  },
  basicInputs: {
    ids: createBasicControl("ids", "Search by ID", "ID"),
    userIds: createBasicControl("userIds", "Search by User ID", "User ID"),
    profileIds: createBasicControl(
      "profileIds",
      "Search by Profile ID",
      "Profile ID"
    ),
    engagementIdentifier: createBasicControl(
      "engagementIdentifier",
      "Search by External ID",
      "External ID"
    ),
  },
  dates: {
    from: createDateControl("engagementDateFrom", "From Date (Performance Date)", {
      setToStartOfDay: true,
      maximum: new Date(),
    }),
    to: createDateControl("engagementDateTo", "To Date (Performance Date)", {
      setToEndOfDay: true,
      maximum: new Date(),
    }),
    fromSimple: createDateControl(
      "engagementDateFrom",
      "From Date (Performance Date)",
      {
        setToStartOfDay: true,
        maximum: new Date(),
      }
    ),
    toSimple: createDateControl("engagementDateTo", "To Date (Performance Date)", {
      setToEndOfDay: true,
      maximum: new Date(),
    }),
  },
  selects: {
    status: createMultiSelectControl("engagementStatus", "Status", "Status"),
    activityType: createMultiSelectControl(
      "engagementType",
      "Activity Name",
      "Activity Name"
    ),
    channel: createMultiSelectControl(
      "channel",
      "Select Channel",
      "Channel",
      "multiSelect"
    ),
  },
};

const withoutDebounceFilterJsonSchema = {
  type: "object",
  properties: {
    engagementDateFrom: commonProperties.engagementDateFrom,
    engagementDateTo: commonProperties.engagementDateTo,
    engagementStatus: commonProperties.engagementStatus,
  },
};

const sharedUISchema = {
  withDebounce: {
    type: "HorizontalLayout",
    elements: [
      commonUIElements.searchInputs.ids,
      commonUIElements.searchInputs.profileIds,
    ],
  },
  withoutDebounce: {
    type: "HorizontalLayout",
    elements: [commonUIElements.selects.status],
  },
};

export const engagementFilterSchemas = {
  "performances-list": {
    parentClassName: "all-debounced-nondebounced-style",
    jsonSchema: {
      withDebounce: {
        type: "object",
        properties: {
          userIds: commonProperties.userIds,
          engagementIdentifier: commonProperties.engagementIdentifier,
          profileIds: commonProperties.profileIds,
        },
      },
      withoutDebounce: {
        type: "object",
        properties: {
          campaignIds: {
            type: "array",
          },
          engagementDateFrom: commonProperties.engagementDateFrom,
          engagementDateTo: commonProperties.engagementDateTo,
          engagementType: commonProperties.engagementType,
          engagementStatus: commonProperties.engagementStatus,
        },
      },
    },
    uiSchema: {
      withDebounce: {
        type: "HorizontalLayout",
        elements: [
          commonUIElements.searchInputs.userIds,
          commonUIElements.searchInputs.engagementIdentifier,
          commonUIElements.searchInputs.profileIds,
        ],
      },
      withoutDebounce: {
        type: "HorizontalLayout",
        elements: [
          createBasicControl("campaignIds", "Campaign", "Campaign", {
            component: "campaignNameSelect",
            selection: "single",
          }),
          commonUIElements.dates.from,
          commonUIElements.dates.to,
          commonUIElements.selects.activityType,
          commonUIElements.selects.status,
        ],
      },
    },
  },
  "package-scan": {
    parentClassName: "all-debounced-nondebounced-style",
    jsonSchema: {
      withDebounce: {
        type: "object",
        properties: {
          ids: createStringProperty(""),
          userIds: {
            type: "array",
          },
          profileIds: {
            type: "array",
          },
          productSkus: {
            type: "array",
            title: "SKU",
          },
          packageCode: createStringProperty("Code"),
        },
      },
      withoutDebounce: withoutDebounceFilterJsonSchema,
    },
    uiSchema: {
      ...sharedUISchema,
      withDebounce: {
        ...sharedUISchema.withDebounce,
        elements: [
          { ...sharedUISchema.withDebounce.elements[0] },
          createSearchControl("userIds", "Search by User ID", "", {
            debounce: true,
          }),
          createSearchControl("profileIds", "Search by Profile ID", "", {
            debounce: true,
          }),
          createSearchControl("packageCode", "Search by Code", "", {
            debounce: true,
          }),
          createSearchControl("productSkus", "Search by SKU", "", {
            debounce: true,
          }),
        ],
      },
      withoutDebounce: {
        ...sharedUISchema.withoutDebounce,
        elements: [
          commonUIElements.dates.from,
          commonUIElements.dates.to,
          ...sharedUISchema.withoutDebounce.elements,
        ],
      },
    },
  },
  "code-scan-generic": {
    parentClassName: "all-debounced-nondebounced-style",
    jsonSchema: codeScanFilterJsonSchema,
    uiSchema: codeScanFilterUiSchema,
  },
  "check-in": {
    jsonSchema: {
      withDebounce: {
        type: "object",
        properties: {
          ids: createStringProperty(""),
          profileIds: commonProperties.profileIds,
        },
      },
      withoutDebounce: withoutDebounceFilterJsonSchema,
    },
    uiSchema: sharedUISchema,
  },
};

export const receiptScanPerformancesFilterJsonSchema = {
  withDebounce: {
    type: "object",
    properties: {
      ids: createStringProperty(),
      engagementIdentifier: createStringProperty(),
      userIds: createStringProperty(),
      profileIds: createStringProperty(),
    },
  },
  withoutDebounce: withoutDebounceFilterJsonSchema,
};

export const receiptScanPerformancesFilterUiSchema = {
  withDebounce: {
    type: "HorizontalLayout",
    elements: [
      commonUIElements.searchInputs.ids,
      commonUIElements.searchInputs.engagementIdentifier,
      commonUIElements.searchInputs.userIds,
      commonUIElements.searchInputs.profileIds,
    ],
  },
  withoutDebounce: {
    type: "HorizontalLayout",
    elements: [
      {
        ...commonUIElements.dates.from,
        scope: "#/properties/engagementDateFrom",
      },
      {
        ...commonUIElements.dates.to,
        scope: "#/properties/engagementDateTo",
        minimum: "fromDate",
      },
      { ...commonUIElements.selects.status, placeholder: "Status" },
    ],
  },
};

export const receiptScanPerformancesFilterAllJsonSchema = {
  type: "object",
  properties: {
    ids: commonProperties.ids,
    userIds: commonProperties.userIds,
    profileIds: commonProperties.profileIds,
    campaignNames: createStringProperty(),
    engagementDateFrom: commonProperties.engagementDateFrom,
    channel: commonProperties.channel,
    engagementDateTo: commonProperties.engagementDateTo,
    engagementStatus: commonProperties.engagementStatus,
    engagementIdentifier: commonProperties.engagementIdentifier,
    vendorName: createStringProperty(),
    minFraudScore: {
      type: "number",
      title: "Min Fraud Score",
    },
    maxFraudScore: {
      type: "number",
      title: "Max Fraud Score",
    },
  },
};
export const receiptScanPerformancesFilterAllUiSchema = {
  type: "VerticalLayout",
  elements: [
    commonUIElements.basicInputs.ids,
    commonUIElements.basicInputs.engagementIdentifier,
    commonUIElements.basicInputs.userIds,
    commonUIElements.basicInputs.profileIds,
    { ...commonUIElements.selects.channel, placeholder: "Channel" },
    createBasicControl("campaignNames", "Search by Campaign Name", "Campaign Name"),
    commonUIElements.dates.from,
    commonUIElements.dates.to,
    commonUIElements.selects.status,
    createBasicControl("vendorName", "Search by Retailer", "Retailer"),
    createBasicControl("minFraudScore", "Min Fraud Score", "Min Fraud Score", {
      step: 0.1,
      min: 0,
      max: 1,
    }),
    createBasicControl("maxFraudScore", "Max Fraud Score", "Max Fraud Score", {
      step: 0.1,
      min: 0,
      max: 1,
    }),
  ],
};

const sharedFilterAllUISchema = {
  type: "VerticalLayout",
  elements: [
    commonUIElements.basicInputs.ids,
    commonUIElements.basicInputs.userIds,
    commonUIElements.basicInputs.engagementIdentifier,
    commonUIElements.basicInputs.profileIds,
    commonUIElements.selects.channel,
    createBasicControl("campaignIds", "Campaign", "Campaign", {
      component: "campaignNameSelect",
      selection: "multiple",
    }),
    { ...commonUIElements.dates.fromSimple, label: "From Date" },
    { ...commonUIElements.dates.toSimple, label: "To Date" },
    commonUIElements.selects.status,
  ],
};

export const engagementAllFiltersSchemas = {
  "performances-list": {
    jsonSchema: {
      type: "object",
      properties: {
        ids: commonProperties.ids,
        profileIds: commonProperties.profileIds,
        userIds: commonProperties.userIds,
        engagementIdentifier: commonProperties.engagementIdentifier,
        channel: {
          type: "array",
          items: {
            type: "string",
            options: createChannelOptions(),
          },
        },
        campaignIds: {
          type: "array",
        },
        engagementDateFrom: commonProperties.engagementDateFrom,
        engagementDateTo: commonProperties.engagementDateTo,
        engagementType: commonProperties.engagementType,
        engagementStatus: {
          type: "array",
          items: {
            type: "string",
            options: createStatusOptions(),
          },
          title: "Status",
        },
      },
    },
    uiSchema: {
      type: "VerticalLayout",
      elements: [
        createBasicControl("campaignIds", "Campaign", "Campaign", {
          component: "campaignNameSelect",
          selection: "single",
        }),
        { ...commonUIElements.dates.fromSimple, label: "From Date" },
        { ...commonUIElements.dates.toSimple, label: "To Date" },
        commonUIElements.basicInputs.profileIds,
        commonUIElements.basicInputs.userIds,
        commonUIElements.basicInputs.engagementIdentifier,
        commonUIElements.selects.activityType,
        commonUIElements.selects.status,
      ],
    },
  },
  "package-scan": {
    jsonSchema: {
      type: "object",
      properties: {
        ids: {
          type: "array",
          title: "ID",
        },
        engagementIdentifier: {
          type: "string",
          title: "External ID",
        },
        userIds: {
          type: "array",
          title: "User ID",
        },
        profileIds: {
          type: "array",
          title: "Profile ID",
        },
        channel: commonProperties.channel,
        productSkus: {
          type: "array",
          title: "SKU",
        },
        campaignIds: {
          type: "string",
          title: "Campaign Name",
        },
        engagementStatus: {
          type: "array",
          items: {
            type: "string",
            options: createStatusOptions(),
          },
          title: "Status",
        },
        engagementDateFrom: {
          type: "string",
          title: "From Date",
        },
        engagementDateTo: {
          type: "string",
          title: "To Date",
        },
        packageCode: {
          type: "string",
          title: "Code",
        },
      },
    },
    uiSchema: {
      type: "VerticalLayout",
      className: "performance-all-filters",
      elements: [
        createBasicControl("ids", "Search by ID", "ID"),
        createBasicControl(
          "engagementIdentifier",
          "Search by External ID",
          "External ID"
        ),
        createBasicControl("userIds", "Search by User ID", "User ID"),
        createBasicControl("profileIds", "Search by Profile ID", "Profile ID"),
        { ...commonUIElements.selects.channel, placeholder: "Select Channel" },
        createBasicControl("campaignIds", "Campaign", "Campaign", {
          component: "campaignDropdownSelect",
        }),
        createDateControl("engagementDateFrom", "Performance Date From", {
          maximum: new Date(),
        }),
        createDateControl("engagementDateTo", "Performance Date To", {
          setToEndOfDay: true,
          minimum: "fromDate",
          maximum: new Date(),
        }),
        commonUIElements.selects.status,
        createBasicControl("productSkus", "Search by SKU", "SKU"),
        createBasicControl("packageCode", "Search by Code", "Code"),
      ],
    },
  },
  "code-scan-generic": {
    jsonSchema: codeScanFilterAllJsonSchema,
    uiSchema: codeScanFilterAllUiSchema,
  },
  "check-in": {
    jsonSchema: {
      type: "object",
      properties: {
        ids: commonProperties.ids,
        profileIds: commonProperties.profileIds,
        campaignIds: {
          type: "array",
        },
        engagementDateFrom: commonProperties.engagementDateFrom,
        engagementDateTo: commonProperties.engagementDateTo,
        identifier: {
          type: "string",
          title: "Referral Identifier",
        },
        engagementStatus: commonProperties.engagementStatus,
      },
    },
    uiSchema: {
      type: "VerticalLayout",
      elements: [
        ...sharedFilterAllUISchema.elements,
        {
          type: "Control",
          scope: "#/properties/identifier",
          placeholder: "Search by identifier",
          label: "Identifier",
        },
      ],
    },
  },
};
